import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import { getAddressFromMagicKey } from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import { MAX_POI } from './GlobalMapSearch.constants';
import type { PlaceCandidate } from './GlobalMapSearch.types';
import { normalizePlaceCoords } from './GlobalMapSearch.utils';

export type ThunkDispatch = (
  action: unknown,
) => Promise<Record<string, unknown>>;

export interface SuggestEntry {
  text: string;
  magicKey: string;
  _serviceUrl?: string;
  _token?: string | null;
}

interface FetchPOIArgs {
  text: string;
  lng: number;
  lat: number;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
}

/**
 * Fetch POI candidates — local geocoder first, then ESRI world to fill remaining slots.
 */
export async function fetchPOICandidates({
  text,
  lng,
  lat,
  serviceUrl,
  token,
  signal,
}: FetchPOIArgs): Promise<PlaceCandidate[]> {
  const baseParams = {
    singleLine: text.toLowerCase(),
    location: `${lng},${lat}`,
    category: 'POI',
    outFields: '*',
    f: 'json',
  };

  let localPOIs: PlaceCandidate[] = [];

  if (serviceUrl) {
    try {
      const params: Record<string, string> = {
        ...baseParams,
        maxLocations: String(MAX_POI),
      };
      if (token) params.token = token;
      const res = await fetch(
        `${serviceUrl}/findAddressCandidates?${new URLSearchParams(params)}`,
        { signal },
      );
      if (res.ok) {
        const json = await res.json();
        localPOIs = (json?.candidates as PlaceCandidate[] | undefined) || [];
      }
    } catch {
      // local failed — fall through to ESRI world
    }
  }

  if (localPOIs.length >= MAX_POI) return localPOIs;

  try {
    const remaining = MAX_POI - localPOIs.length;
    const qs = new URLSearchParams({
      ...baseParams,
      maxLocations: String(remaining),
    });
    const res = await fetch(
      `${ESRI_GEOCODE_SERVICE_URL}/findAddressCandidates?${qs}`,
      { signal },
    );
    if (!res.ok) return localPOIs;
    const json = await res.json();
    const esriPOIs = (json?.candidates as PlaceCandidate[] | undefined) || [];
    return [...localPOIs, ...esriPOIs].slice(0, MAX_POI);
  } catch {
    return localPOIs;
  }
}

interface ResolveSuggestionsArgs {
  suggestions: SuggestEntry[];
  seen: Set<string>;
  dispatch: ThunkDispatch;
  signal: AbortSignal;
}

/**
 * Resolve /suggest results into full PlaceCandidates via magicKey lookup,
 * skipping any addresses already in `seen`.
 */
export async function resolveSuggestions({
  suggestions,
  seen,
  dispatch,
  signal,
}: ResolveSuggestionsArgs): Promise<PlaceCandidate[]> {
  const results = await Promise.all(
    suggestions
      .filter(s => s.magicKey && !seen.has(s.text.toLowerCase()))
      .map(async s => {
        try {
          const res = (await dispatch(
            getAddressFromMagicKey.initiate({
              magicKey: s.magicKey,
              text: s.text,
              _serviceUrl: s._serviceUrl,
              _token: s._token,
            }),
          )) as unknown as { data?: PlaceCandidate };
          if (!res?.data?.location) return null;
          return normalizePlaceCoords(res.data);
        } catch {
          return null;
        }
      }),
  );

  if (signal.aborted) return [];

  const resolved: PlaceCandidate[] = [];
  for (const r of results) {
    if (!r?.address || !r.location) continue;
    const key = r.address.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push(r);
  }
  return resolved;
}
